import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import OrderItemDTO from "@/dto/order/OrderItemDTO";
import OrderStatus from "@/enum/OrderStatus";
import ShopType from "@/enum/ShopType";
import NewOrderItemDisplayType from "@/scenes/OrderManagement/enum/NewOrderItemDisplayType";
import OrderItemTitleExternalIdPropElement from "../OrderItemsColumn/OrderItemTitleExternalIdPropElement";
import OrderItemDisplayNoPic from "./OrderItemDisplayNoPic";
import OrderItemDisplayWithPic from "./OrderItemDisplayWithPic";
import OrderItemDisplayOnlyShowPic from "./OrderItemDisplayOnlyShowPic";

export interface OrderItemDisplayConfig {
    showGift: boolean;
    maxDisplayOrderItemCount: number;
    selectedOrderItemDisplayTypesWithOutGiftTypes: NewOrderItemDisplayType[];
    picSize: number;
}

interface OrderItemDisplayProps {
    orderItems: OrderItemDTO[];
    orderStatus: OrderStatus;
    shopType: ShopType;
    shopId: number;
    hasAbnormal: boolean;
    displayConfig: OrderItemDisplayConfig;
}

const OrderItemDisplay: React.FC<OrderItemDisplayProps> = observer(function (props) {
    const { orderItems, shopType, orderStatus, hasAbnormal, displayConfig } = props;
    const [isExpanded, setIsExpanded] = useState(false);
    // 基础配置
    const showGift = displayConfig.showGift;
    const max = displayConfig.maxDisplayOrderItemCount;
    const picSize = displayConfig.picSize;

    const showPic = displayConfig.selectedOrderItemDisplayTypesWithOutGiftTypes.includes(NewOrderItemDisplayType.PIC);

    const showTitle = displayConfig.selectedOrderItemDisplayTypesWithOutGiftTypes.includes(
        NewOrderItemDisplayType.TITLE,
    );
    const showSimpleTitle = displayConfig.selectedOrderItemDisplayTypesWithOutGiftTypes.includes(
        NewOrderItemDisplayType.SIMPLE_TITLE,
    );
    const showExternalId = displayConfig.selectedOrderItemDisplayTypesWithOutGiftTypes.includes(
        NewOrderItemDisplayType.EXTERNAL_ID,
    );
    const showSkuProperties = displayConfig.selectedOrderItemDisplayTypesWithOutGiftTypes.includes(
        NewOrderItemDisplayType.SKU_PROPERTIES,
    );

    // 商品统计
    const giftOrderItems = useMemo(() => orderItems.filter((item) => item.isGift), [orderItems]);
    const giftOrderItemCount = giftOrderItems.length;
    const visibleItemCount = showGift ? orderItems.length : orderItems.length - giftOrderItemCount;
    const orderItemCount = orderItems.length;

    // 显示类型判断
    const onlyShowPicType = displayConfig.selectedOrderItemDisplayTypesWithOutGiftTypes.length === 1 && showPic;
    const noPicAndSelectOtherType = !showPic && displayConfig.selectedOrderItemDisplayTypesWithOutGiftTypes.length >= 1;

    // 处理后的商品列表
    const displayOrderItems = useMemo(() => {
        let itemsToDisplay = orderItems.filter((item) => {
            // 如果不显示赠品，且当前商品是赠品，且订单不全是赠品，则过滤掉
            if (!showGift && item.isGift && orderItemCount !== giftOrderItemCount) {
                return false;
            }
            return true;
        });

        // 如果有max值且未展开，则只显示前max个
        if (max && max > 0 && !isExpanded && itemsToDisplay.length > max) {
            itemsToDisplay = itemsToDisplay.slice(0, max);
        }

        return itemsToDisplay;
    }, [orderItems, max, isExpanded, showGift, giftOrderItemCount]);

    // 是否显示展开按钮
    const shouldShowToggle = max && max > 0 && visibleItemCount > max;

    const handleToggle = () => setIsExpanded(!isExpanded);

    // 当max或showGift改变时重置展开状态
    useEffect(() => {
        setIsExpanded(false);
    }, [max, showGift]);

    return (
        <div className="ec-order-item-display">
            {displayOrderItems.map((orderItem) => {
                const hideGift = !showGift && orderItem.isGift && orderItemCount !== giftOrderItemCount;

                if (hideGift) {
                    return null;
                }

                if (onlyShowPicType) {
                    return (
                        <OrderItemDisplayOnlyShowPic
                            key={orderItem.id}
                            orderItem={orderItem}
                            orderStatus={orderStatus}
                            shopType={shopType}
                            hasAbnormal={hasAbnormal}
                            picSize={picSize}
                        />
                    );
                }

                if (noPicAndSelectOtherType) {
                    return (
                        <OrderItemDisplayNoPic
                            key={orderItem.id}
                            orderItem={orderItem}
                            orderStatus={orderStatus}
                            showTitle={showTitle}
                            showSimpleTitle={showSimpleTitle}
                            showExternalId={showExternalId}
                            showSkuProperties={showSkuProperties}
                        />
                    );
                }

                if (showPic) {
                    return (
                        <OrderItemDisplayWithPic
                            key={orderItem.id}
                            orderItem={orderItem}
                            orderStatus={orderStatus}
                            shopType={shopType}
                            hasAbnormal={hasAbnormal}
                            showTitle={showTitle}
                            showSimpleTitle={showSimpleTitle}
                            showExternalId={showExternalId}
                            showSkuProperties={showSkuProperties}
                            picSize={picSize}
                        />
                    );
                }

                /**
                 * 兜底显示
                 */
                return (
                    <OrderItemTitleExternalIdPropElement
                        key={orderItem.id}
                        orderItem={orderItem}
                        orderStatus={orderStatus}
                        hasAbnormal={hasAbnormal}
                    />
                );
            })}
            {shouldShowToggle && (
                <div
                    style={{
                        cursor: "pointer",
                        textAlign: "center",
                        padding: "8px 0",
                        borderTop: "1px solid #f0f0f0",
                        color: "#337ab7",
                        fontSize: "11px",
                    }}
                    onClick={handleToggle}
                >
                    {isExpanded
                        ? "收起商品"
                        : `显示所有商品(${showGift ? orderItems.length : orderItems.length - giftOrderItemCount})`}
                </div>
            )}
        </div>
    );
});

export default OrderItemDisplay;
